import { usePrevious, useViewportSize } from '@mantine/hooks'
import { LineChartBarSquare } from '@qr-front/icons/line-chart-bar-square'
import { LineChevronLeftSmall } from '@qr-front/icons/line-chevron-left-small'
import { LineCreditCards } from '@qr-front/icons/line-credit-cards'
import { LineLogOut } from '@qr-front/icons/line-log-out'
import { LineMenu } from '@qr-front/icons/line-menu'
import { LineQrType } from '@qr-front/icons/line-qr-type'
import { LineSupport } from '@qr-front/icons/line-support'
import { LineUser } from '@qr-front/icons/line-user'
import { theme } from '@qr-front/ui'
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	sidebarMenuButtonLabelClasses,
	SidebarMenuItem,
	SidebarTrigger,
	useSidebar,
} from '@qr-front/ui/dashboard/components/Sidebar'
import { cn, pxStringToNumber } from '@qr-front/ui/utils'
import { ROUTES } from 'constants/routesConstants'
import { logOut } from 'features/auth/utils/logOut'
import { qrSvgPromiseCache } from 'features/library/client'
import { useTranslations } from 'next-intl'
import { useEffect, useRef } from 'react'

import { LogoLink } from '@/components/LogoLink/LogoLink'
import { Link, usePathname, useRouter } from '@/i18n-routing'

import { useGatedNavigation } from '../hooks/useGatedNavigation'

export const sidebarLinkGroup = [
	{
		title: 'dashboard__sidebar__link_groups__analytics',
		url: ROUTES.cabinet.analytics,
		icon: LineChartBarSquare,
		prefetch: true,
	},
	{
		title: 'dashboard__sidebar__link_groups__qr_codes',
		url: ROUTES.cabinet.qrCodes,
		icon: LineQrType,
	},
	{
		title: 'dashboard__sidebar__link_groups__account',
		url: ROUTES.cabinet.account,
		icon: LineUser,
	},
	{
		title: 'dashboard__sidebar__link_groups__billing',
		url: ROUTES.cabinet.billing,
		icon: LineCreditCards,
	},
]

export const sidebarFooterLinkGroup = [
	{
		title: 'dashboard__sidebar__link_groups__help',
		url: ROUTES.cabinet.help,
		icon: LineSupport,
	},
	{
		title: 'dashboard__sidebar__link_groups__logout',
		icon: LineLogOut,
	},
]

export const PrivateLayoutSidebar = () => {
	const t = useTranslations()
	const pathname = usePathname()
	const router = useRouter()
	const sidebar = useSidebar()
	const { width: viewportWidth } = useViewportSize()
	const prevViewportWidth = usePrevious(viewportWidth)
	const savedUserStateRef = useRef<boolean | null>(null)
	const handleGatedNavigation = useGatedNavigation()

	const shouldForceCollapse =
		viewportWidth >= pxStringToNumber(theme.screens.desktop) &&
		viewportWidth < pxStringToNumber(theme.screens.desktop) + 245

	const prevShouldForceCollapseRef = useRef<boolean>(shouldForceCollapse)

	useEffect(() => {
		if (viewportWidth === prevViewportWidth) {
			return
		}

		const wasInForcedRange = prevShouldForceCollapseRef.current
		const isInForcedRange = shouldForceCollapse

		// Entering the forced collapse range: save current state and force collapse
		if (!wasInForcedRange && isInForcedRange) {
			savedUserStateRef.current = sidebar.open
			sidebar.setOpen(false)
		}
		// Leaving the forced collapse range: restore saved state
		else if (
			wasInForcedRange &&
			!isInForcedRange &&
			savedUserStateRef.current !== null
		) {
			sidebar.setOpen(savedUserStateRef.current)
			savedUserStateRef.current = null
		}

		prevShouldForceCollapseRef.current = isInForcedRange
	}, [prevViewportWidth, shouldForceCollapse, sidebar, viewportWidth])

	const handleLogout = async () => {
		qrSvgPromiseCache.clear()
		await logOut()
	}

	const handleMouseEnter = (item: (typeof sidebarLinkGroup)[number]) => {
		router.prefetch(item.url)
	}

	const header = (
		<SidebarHeader>
			<SidebarGroup className="hidden group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:items-center">
				<SidebarTrigger className="flex h-8 items-center justify-center">
					<LineMenu />
				</SidebarTrigger>
			</SidebarGroup>
			<SidebarGroup className="flex-row justify-between gap-2 group-data-[collapsible=icon]:hidden">
				<LogoLink href={ROUTES.cabinet.qrCodes} />
				<SidebarTrigger>
					<span className="flex size-6 items-center justify-center rounded-8 bg-light-blue">
						<LineChevronLeftSmall className="size-4 shrink-0" />
					</span>
				</SidebarTrigger>
			</SidebarGroup>
		</SidebarHeader>
	)

	return (
		<Sidebar>
			{!shouldForceCollapse ? header : null}
			<SidebarContent>
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu>
							{sidebarLinkGroup.map((item) => (
								<SidebarMenuItem key={item.title}>
									<SidebarMenuButton asChild isActive={item.url === pathname}>
										<Link
											href={item.url}
											prefetch={!item.prefetch}
											onClick={(event) =>
												handleGatedNavigation(event, item.url)
											}
											onMouseEnter={
												item.prefetch ? () => handleMouseEnter(item) : undefined
											}
										>
											<item.icon className="size-6 shrink-0" />
											<span className={sidebarMenuButtonLabelClasses}>
												{t(item.title)}
											</span>
										</Link>
									</SidebarMenuButton>
								</SidebarMenuItem>
							))}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>
			<SidebarFooter>
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu>
							{sidebarFooterLinkGroup.map((item) => (
								<SidebarMenuItem key={item.title}>
									<SidebarMenuButton asChild isActive={item.url === pathname}>
										{item.url ? (
											<Link href={item.url}>
												<item.icon className="shrink-0" />
												<span className={sidebarMenuButtonLabelClasses}>
													{t(item.title)}
												</span>
											</Link>
										) : (
											<button className="w-full" onClick={handleLogout}>
												<item.icon className="size-6 shrink-0" />
												<span
													className={cn(
														sidebarMenuButtonLabelClasses,
														'text-left',
													)}
												>
													{t(item.title)}
												</span>
											</button>
										)}
									</SidebarMenuButton>
								</SidebarMenuItem>
							))}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarFooter>
		</Sidebar>
	)
}
